/**
 * Upload class is used for uploading file(s).
 * There are two requests, first one is `initialData` request which should return unique parameter so we could
 * associate unique parameter with file(s) which is/are going to be uploaded. Operation expects to have `params` for
 * initial request and `uploadParams` object which will contain file(s) we are going to upload.
 *
 * @author Branislav Bogdanovic
 *
 * Example:
 * {
    data: {
      template: {
        bind: {
          concurrently: "{concurrently}"
        },
        method: "get",
        url: "/upload/files"
      }
    },
    mappingName: "uploadStatus",
    initialData: {
      method: "get",
      url: "/upload/initialize_file",
      usePrefix: RestApiPrefix.none
    },
    dependent: false,
    showProgress: true,
    uploadCondition: "success",
    afterInitialRequest: [{
      moduleName: "App",
      name: "changeSavedLanguage",
      type: OperationType.addOperationData
    }],
    operations: [{
      moduleName: "Upload",
      name: "setUploadStatus",
      args: ["uploadStatus"]
    }],
    progressOperations: [{
      moduleName: "DropUpload",
      args: ["progresses"],
      name: "setUploadProgress"
    }],
    handleFileRejection: [{
      className: 'sm warning',
      items: [{
        btnConfig: {
          fillMode: 'flat',
          pos: Position.end
        },
        btnName: 'REJECTED_FILES.close',
        close: true
      }],
      text: 'REJECTED_FILES.text',
      title: 'REJECTED_FILES.title',
      warningType: WarningType.warning,
      type: OperationType.warning
    }],
    responseFiles: {
      rejected: 'rejectedFiles',
      uploaded: 'uploadedFiles'
    },
    uniqueParameter: "folderName",
    type: OperationType.upload
  }
 */
import * as _ from 'lodash';
import uuid from 'uuid-random';
import {
  ASYNC_UPLOAD_DISPATCHER_NAME,
  INITIAL_UPLOAD_REQUEST_MAPPING_NAME_SUFFIX,
  UPLOAD_REQUEST_DEFAULT_MAPPING_NAME
} from '../config/Constants';
import { DispatcherManager } from '../Dispatcher';
import OperationError from './OperationError';
import {
  Action,
  ConditionOperation,
  Operation,
  OperationType,
  RequestData,
  RequestOperation,
  UploadOperation
} from '../Types';
import ComplexOperationToAction from './ComplexOperationToAction';

type ResponseFile = {
  /**
   * Rejected files from server
   */
  rejected: string;
  /**
   * Uploaded files from server
   */
  uploaded: string;
};

class Upload extends ComplexOperationToAction {
  /**
   * This property gets value from `initialData` response (e.g. server returns property `success:boolean` which indicates weather or not to upload files => uploadCondition: 'success').
   */
  private _uploadCondition: string;
  /**
   * If `true` then upload widget will be shown.
   */
  private _showProgress: boolean;
  /**
   * Internal property which is used to make all operations after ping request dependent on that request.
   * @internalProperty
   */
  private _dependence: string | string[];
  /**
   * Represents a name under the server response data will be saved.
   * Although we have two kind of requests, data of both responses will not affected each other.
   * By default, if you don't set mappingName, then response data of `data` request will be saved under name `uploadStatus`,
   * and response data of `initialData` request will be saved under name `uploadStatus_INITIAL`.
   */
  private _mappingName: string;
  /**
   * Internal property used to distinguish two different response objects from two requests.
   * For more info look at mappingName definition.
   * @internalProperty
   */
  private _initialMappingName: string;
  /**
   * Represents mapping of server responses for rejected files. By default successfully uploaded files will be in
   * server response property 'uploadedFiles' and rejectedFiles will be in property 'rejectedFiles'.
   * If server returns different mappings then add rejected and uploaded names.
   */
  private _responseFiles: ResponseFile;
  /**
   * Represents retrieved parameter from `initialData` response.
   * (e.g. it might contains folder name where server will save all uploading files).
   * This param is used to send to server on `data` request.
   */
  private _uniqueParameter: string;
  /**
   * Represents object for initial request.
   * If `initialData` object properties are not set, then initial data will be same as `data` object request. In
   * response, server returns some data which is used in data object request.
   */
  private _initialData: RequestData;
  /**
   * `data` object prop is defined when adding a new operation, more precisely, when need to send data to dispatcher
   * operation data so that data can be used by current operation method or in the next operation(s) in current
   * action. In data property, through template/update object, it's possible to add/update data to/from dispatcher
   * operation data. In that case, all added/updated dispatcher data can be used through current operation method or
   * in the next operation(s) in current action.
   * In this operation type, data object contains predefined properties `method` (method for ajax request),
   * `params` (params data sent to server in ajax request), `uploadParams` (send uploading files to server) and
   * `url` (url for ajax request).
   */
  private _data: any;
  /**
   * Operations which are executed after `data` request, more precisely after file(s) is/are uploaded.
   */
  private _operations: Action;
  /**
   * Operations which are executed if some files are rejected by server.
   */
  private _handleFileRejection: Action;
  /**
   * Represents array of operation(s) which are executed in progress bar.
   */
  private _progressOperations: Action;
  /**
   * Represents array of operation(s) which are executed after initial request.
   */
  private _afterInitialRequest: Action;
  /**
   * Represents array of operation(s) which are executed after initial request if there are request error which we want to handle by handleErrorStatuses.
   */
  private _afterInitialRequestHandleErrorStatus: Action;

  /**
   * Represents array of operation(s) which are executed after ping request if there are request error which we want to handle by handleErrorStatuses.
   */
  private _afterPingRequestHandleErrorStatus: Action

  /**
   * Represents moduleId of module component in which file are chosen/dropped for upload.
   * uploadGroupId is mandatory parameter and it's passed via operation data!
   */
  private _uploadGroupId: string;

  /**
   * Initial request param isUpload is false if prepareRequestIsNoUpload is true and vice versa
   */
  private prepareRequestIsNoUpload: boolean;

  /**
   * Without prepare request
   */
  private isUploadFilesRequestSimple: boolean;

  /**
   * Sets initialize data.
   * @param operation UploadOperation
   * @param appOperationData Data that is currently on dispatcher.
   * @param appDispatcherId Current dispatcher id.
   * @param templateData Data from operation that is evaluated.
   * @param isUploadFilesRequestSimple Without prepare request.
   */
  constructor(operation: UploadOperation, appOperationData: any, appDispatcherId: string, templateData: { data: any, initialData: any }, isUploadFilesRequestSimple?: boolean) {
    super(operation, appDispatcherId);
    const {
      uploadCondition,
      showProgress = false,
      dependence = uuid(),
      mappingName = UPLOAD_REQUEST_DEFAULT_MAPPING_NAME,
      responseFiles = {
        rejected: 'rejectedFiles',
        uploaded: 'uploadedFiles'
      },
      uniqueParameter,
      handleErrorStatuses
    } = operation;
    this.uploadCondition = uploadCondition;
    this.showProgress = showProgress;
    this.dependence = dependence;
    this.mappingName = mappingName;
    this.responseFiles = responseFiles;
    this.uniqueParameter = uniqueParameter;
    this.handleErrorStatuses = handleErrorStatuses;

    this.initialMappingName = mappingName + INITIAL_UPLOAD_REQUEST_MAPPING_NAME_SUFFIX;

    let {
      initialData = {},
      afterInitialRequest = [],
      data,
      operations = [],
      handleFileRejection = [],
      progressOperations = [],
      afterInitialRequestHandleErrorStatus = [],
      afterPingRequestHandleErrorStatus = []
    } = operation;
    this.afterInitialRequest = afterInitialRequest.map(this.remapMappingNameInArguments);
    this.afterInitialRequestHandleErrorStatus = afterInitialRequestHandleErrorStatus.map(this.remapMappingNameInArguments);
    this.afterPingRequestHandleErrorStatus = afterPingRequestHandleErrorStatus.map(this.remapMappingNameInArguments);
    this.operations = operations.map(this.remapMappingNameInArguments);
    this.handleFileRejection = handleFileRejection.map(this.remapMappingNameInArguments);
    this.progressOperations = progressOperations.map(this.remapMappingNameInArguments);
    this.isUploadFilesRequestSimple = isUploadFilesRequestSimple;

    // <debug>
    if (!uniqueParameter) {
      throw new OperationError('Please provide "uniqueParameter" to operation.', operation);
    }
    let { uploadParams, params, uploadGroupId, prepareRequestIsNoUpload } = appOperationData;
    this.prepareRequestIsNoUpload = prepareRequestIsNoUpload;
    this.uploadGroupId = uploadGroupId;
    if (!this.uploadGroupId) {
      throw new Error('Please provide `uploadGroupId` with value of `moduleId` from module component where files are' +
        ' uploaded.');
    }
    let msg = `\nthis.dispatch("${this.actionName}", Position.end, {
      params: { /** params configuration here*/},
      uploadParams: { /** uploadParams configuration here*/},
      ...otherData
    });\n\n`;
    if (!params) {
      // params is used for initial request
      throw new OperationError(`Please provide "params" data for data request.\nExample:${msg}`, operation);
    } else {
      uploadParams.extract = params.extractArchive;
    }
    if (!uploadParams && !JSON.stringify(this.afterInitialRequest).match(/uploadParams/)) {
      // uploadParams is used for request, should contain file that is uploading
      throw new OperationError(`Please provide "uploadParams" data for initialData request.\nExample:${msg}`, operation);
    }

    this.addOriginalDataToEditor({
      initialData: _.cloneDeep(initialData),
      data: _.cloneDeep(data)
    });
    // </debug>

    this.initialData = templateData.initialData;
    this.data = templateData.data;

    if (_.isEmpty(this.initialData) && !isUploadFilesRequestSimple) {
      // if initialData is not configured, then initial request will use same configuration as request
      this.initialData = { ...this.data };
    }

    // <debug>
    if (_.isEmpty(this.data)) {
      throw new OperationError('Data is mandatory for OperationType.upload; Please provide request configuration.', operation);
    }
    // </debug>
  }

  /**
   * Gets operations based on Upload operation.
   */
  getOperations = (): Action => {
    const dispatcherEditorAdditionalData: any = this.getEditorAdditionalData();
    const { actionName, initialMappingName, uploadCondition, uniqueParameter } = this;

    this.addProgressOperationsToUploadGroupAction();

    let action: Action = this.isUploadFilesRequestSimple ?
      [{
        actionName,
        data: {
          template: {
            bind: {
              progresses: '{progresses} || {}'
            },
            uploadFlowState: {
              step: 'start',
              uploadGroupId: this.uploadGroupId
            }
          }
        },
        persistent: ['uploadFlowState'],
        ...dispatcherEditorAdditionalData,
        type: OperationType.data
      }, {
        moduleId: '{uploadPanelModuleId}',
        evaluate: true,
        executeType: 'visible',
        moduleName: 'UploadPanel',
        args: ['undefined', 'droppedFiles'],
        name: 'setGridConfig'
      }, {
        actionName,
        ...dispatcherEditorAdditionalData,
        // <debug>
        debug: this.debug,
        log: this.log,
        // </debug>,
        condition: '{droppedFiles}',
        conditionOperations: [this.getPingRequest(), this.getAfterPingRequestOperations(this.progressOperations)],
        elseOperations: [{
          actionName,
          name: 'uploadFileMissing',
          ...dispatcherEditorAdditionalData,
          type: OperationType.action
        }],
        ...dispatcherEditorAdditionalData,
        type: OperationType.conditional
      }]
      :
      [{
        actionName,
        data: {
          template: {
            bind: {
              progresses: '{progresses} || {}'
            },
            uploadFlowState: {
              step: 'start',
              uploadGroupId: this.uploadGroupId
            }
          }
        },
        persistent: ['uploadFlowState'],
        ...dispatcherEditorAdditionalData,
        type: OperationType.data
      }, {
        actionName,
        ...dispatcherEditorAdditionalData,
        // <debug>
        debug: this.debug,
        log: this.log,
        // </debug>
        condition: `{uploadParams} && !{uploadParams.${uniqueParameter}}`,
        conditionOperations: [{
          actionName,
          data: {
            ...this.initialData,
            ...this.getAdditionalRequestData(!this.prepareRequestIsNoUpload)
          },
          mappingName: initialMappingName,
          synchronous: true,
          handleErrorStatuses: this.handleErrorStatuses,
          ...dispatcherEditorAdditionalData,
          type: OperationType.request
        }],
        elseOperations: [{
          actionName,
          data: {
            template: {
              [initialMappingName]: {
                bind: {
                  [uploadCondition]: `{uploadParams.${uniqueParameter}}`
                }
              }
            }
          },
          type: OperationType.data
        }],
        type: OperationType.conditional
      }, {
        actionName,
        // <debug>
        debug: this.debug,
        log: this.log,
        // </debug>
        condition: `{${initialMappingName}.${uploadCondition}}`,
        conditionOperations: [...this.getAfterInitialRequest(), {
          actionName,
          ...dispatcherEditorAdditionalData,
          // <debug>
          debug: this.debug,
          log: this.log,
          // </debug>,
          condition: '{uploadParams.file} && {uploadParams.file.length}',
          conditionOperations: [this.getPingRequest(), this.getAfterPingRequestOperations(this.progressOperations)],
          elseOperations: [{
            actionName,
            name: 'uploadFileMissing',
            ...dispatcherEditorAdditionalData,
            type: OperationType.action
          }],
          ...dispatcherEditorAdditionalData,
          type: OperationType.conditional
        }],
        elseOperations: [{
          actionName,
          data: {
            handleErrorStatuses: this.handleErrorStatuses || []
          },
          type: OperationType.data
        }, {
          condition: `{${initialMappingName}}.status && !({handleErrorStatuses}.find((item)=>item==={${initialMappingName}}.status))`,
          conditionOperations: [{
            actionName,
            name: 'uploadFailedWarning',
            ...dispatcherEditorAdditionalData,
            type: OperationType.action
          }],
          elseOperations: [...this.afterInitialRequestHandleErrorStatus].map((o: any) => {
            const a = 'a';
            o.actionName = actionName;
            // <debug> - used only in combination with dispatcher editor
            DispatcherManager.addComplexOperationParentId(o, this.editorData.complexOperationId);
            // </debug>
            return o;
          }),
          ...dispatcherEditorAdditionalData,
          type: OperationType.conditional
        }],
        ...dispatcherEditorAdditionalData,
        type: OperationType.conditional
      }];

    // <debug>
    action.push(...this.getComplexOperationEnd());
    action = action.map((op: any) => DispatcherManager.addComplexOperationParentId(op, this.editorData.complexOperationId));
    // </debug>

    return action;
  };

  /**
   * Used when mapping name is dynamically passed.
   * E.g. From UploadPanel component configuration.
   * @param o
   */
  private remapMappingNameInArguments = (o: Operation) => {
    let initialMappingName: string = `{mappingName${INITIAL_UPLOAD_REQUEST_MAPPING_NAME_SUFFIX}}`;
    if (Array.isArray(o.args) && o.args.some((arg: string) => arg.includes('{mappingName}') || arg.includes(initialMappingName))) {
      const regex: RegExp = new RegExp(initialMappingName, 'g');
      o.args = o.args.map((arg: string) => {
        arg = arg.replace(/{mappingName}/g, this.mappingName);
        arg = arg.replace(regex, this.initialMappingName);
        return arg;
      });
    }
    return o;
  };

  /**
   * Gets start of complex operation for Upload.
   * @param o Upload operation
   * @dispatcherEditor
   */
  public getComplexOperationStart(o: UploadOperation): Operation[] {
    // eslint-disable-next-line array-bracket-newline
    const { original } = this.editorData;
    return [
      // <debug>
      {
        ...this.getComplexOperationStartCommonData(o),
        data: original.data,
        initialData: original.initialData,
        editorData: this.data
      } as any
      // eslint-disable-next-line array-element-newline
      // </debug>
    ];
  }

  /**
   * Get after ping request operations.
   * @param progressOperations
   */
  getAfterPingRequestOperations = (progressOperations: Operation[]): ConditionOperation => {
    const { actionName, dependence, mappingName } = this;
    const dispatcherEditorAdditionalData: any = this.getEditorAdditionalData();
    let afterPingRequestOperations: Action = this.showProgress ? [{
      actionName,
      actionType: {
        dispatcherId: ASYNC_UPLOAD_DISPATCHER_NAME
      },
      data: {
        progresses: {}
      },
      name: 'ShowUploadFileProgresses',
      ...dispatcherEditorAdditionalData,
      type: OperationType.action
    }] : [];
    afterPingRequestOperations.push(...progressOperations, {
      actionName,
      ...dispatcherEditorAdditionalData,
      condition: `{${mappingName}} && {${mappingName}.multipleResponses}`,
      conditionOperations: [{
        actionName,
        internalOperation: true,
        data: {
          responseFiles: this.responseFiles
        },
        type: OperationType.data
      }, {
        actionName,
        internalOperation: true,
        data: {
          template: {
            bind: {
              group: `this.groupFiles("${mappingName}")`
            }
          }
        },
        type: OperationType.data
      }],
      type: OperationType.conditional
    }, ...this.getOperationsAfterUploadRequest(), {
      actionName,
      persistent: [mappingName],
      internalOperation: true,
      type: OperationType.removePersistentData
    }, {
      actionName,
      data: {
        uploadFlowState: {
          step: 'end',
          uploadGroupId: this.uploadGroupId
        }
      },
      internalOperation: true,
      type: OperationType.data
    }, {
      actionName,
      internalOperation: true,
      condition: '{group?.rejected?.length}',
      conditionOperations: [...this.getHandleFileRejection()],
      type: OperationType.conditional
    });

    // <debug> - used only in combination with dispatcher editor
    afterPingRequestOperations = afterPingRequestOperations.map((o: any) => DispatcherManager.addComplexOperationParentId(o, this.editorData.complexOperationId));
    // </debug>
    const serverStatus = `({${mappingName}_multiErrors} && {${mappingName}_multiErrors}[0] && {${mappingName}_multiErrors}[0].response.status)`;
    const serverStatusIsNot200 = `${serverStatus} && ${serverStatus}!==200`;
    const errorStatusIsHandled = `({handleErrorStatuses}.find((item)=>item===${serverStatus}))`;
    return {
      actionName,
      dependence,
      ...dispatcherEditorAdditionalData,
      condition: serverStatusIsNot200,
      conditionOperations: [{
        actionName,
        internalOperation: true,
        data: {
          handleErrorStatuses: this.handleErrorStatuses || []
        },
        type: OperationType.data
      }, {
        actionName,
        ...dispatcherEditorAdditionalData,
        condition: errorStatusIsHandled,
        conditionOperations: [...this.afterPingRequestHandleErrorStatus.map((o: any) => DispatcherManager.addComplexOperationParentId(o, this.editorData.complexOperationId)), ...afterPingRequestOperations],
        type: OperationType.conditional
      }],
      elseOperations: [...afterPingRequestOperations],
      type: OperationType.conditional
    } as ConditionOperation;
  };

  /**
   * Gets additional request data. Contains `uploadGroupId` and `isUpload`.
   * @param {Boolean} isUpload
   */
  private getAdditionalRequestData = (isUpload: boolean = true) => ({
    isUpload: isUpload,
    uploadGroupId: this.uploadGroupId
  });

  /**
   * Gets ping request.
   */
  private getPingRequest(): RequestOperation {
    return {
      actionName: this.actionName,
      data: {
        ...this.data,
        ...this.getAdditionalRequestData()
      },
      mappingName: this.mappingName,
      persistent: true,
      subName: this.dependence,
      synchronous: false,
      handleErrorStatuses: this.handleErrorStatuses,
      ...this.getEditorAdditionalData(),
      type: OperationType.request
    } as RequestOperation;
  }

  /**
   * Adds progressOperations to `DispatcherUploadGroupActions` if progressOperations is passed.
   */
  private addProgressOperationsToUploadGroupAction() {
    if (this.progressOperations.length) {
      const additionalData = {
        actionName: this.actionName,
        independent: true,
        moduleId: this.uploadGroupId
      };
      this.progressOperations = this.progressOperations.map((o: any) => _.assign(o, additionalData));

      DispatcherManager.addOperationsToExistingAction('DispatcherUploadGroupActions', this.progressOperations, this.appDispatcherId);
    }
  }

  /**
   * Gets handleFileRejection operations.
   */
  private getHandleFileRejection(): Operation[] {
    this.handleFileRejection = this.handleFileRejection.reduce((operations: Action, operation: any) => {
      if (operation.moduleName) {
        operation.moduleId = this.uploadGroupId; // add moduleId only to operations that are having moduleName
      }
      operation.actionName = this.actionName;
      // <debug>
      operation.complexParentOperationId = this.editorData.complexOperationId; // used for dispatcher editor
      // operation grouping
      // </debug>
      delete operation.dependent;

      operations.push(operation);
      return operations;
    }, []);
    return this.handleFileRejection;
  }

  /**
   * Gets operations that are passed via Upload operation type.
   */
  private getOperationsAfterUploadRequest(): Operation[] {
    this.operations = this.operations.reduce((operations: Action, operation: any) => {
      if (operation.moduleName) {
        operation.moduleId = this.uploadGroupId; // add moduleId only to operations that are having moduleName
      }
      if (_.isUndefined(operation.dependent) || operation.dependent) {
        // operation.dependence = this.dependence;
      }
      operation.actionName = this.actionName;
      delete operation.dependent;
      operations.push(operation);
      return operations;
    }, []);
    return this.operations;
  }

  /**
   * Gets operations that are executes after initial request.
   */
  private getAfterInitialRequest(): Operation[] {
    const { actionName, uniqueParameter } = this;
    return [{
      data: {
        template: {
          bind: {
            params: '{uploadParams}'
          }
        }
      },
      internalOperation: true,
      type: OperationType.data
    }, {
      data: {
        update: {
          params: {
            bind: {
              [uniqueParameter]: `{${this.initialMappingName}.${uniqueParameter}}`
            }
          }
        }
      },
      internalOperation: true,
      type: OperationType.data
      // eslint-disable-next-line sonarjs/no-identical-functions
    }, ...this.afterInitialRequest].map((o: any) => {
      o.actionName = actionName;
      // <debug> - used only in combination with dispatcher editor
      DispatcherManager.addComplexOperationParentId(o, this.editorData.complexOperationId);
      // </debug>
      return o;
    });
  }

  /**
   * GETTERS AND SETTERS BELLOW
   */

  get dependence(): string | string[] {
    return this._dependence;
  }

  set dependence(value: string | string[]) {
    this._dependence = value;
  }

  get mappingName(): string {
    return this._mappingName;
  }

  set mappingName(value: string) {
    this._mappingName = value;
  }

  get uploadCondition(): string {
    return this._uploadCondition;
  }

  set uploadCondition(value: string) {
    this._uploadCondition = value;
  }

  get responseFiles(): ResponseFile {
    return this._responseFiles;
  }

  set responseFiles(value: ResponseFile) {
    this._responseFiles = value;
  }

  get uniqueParameter(): string {
    return this._uniqueParameter;
  }

  set uniqueParameter(value: string) {
    this._uniqueParameter = value;
  }

  get showProgress(): boolean {
    return this._showProgress;
  }

  set showProgress(value: boolean) {
    this._showProgress = value;
  }

  get initialMappingName(): string {
    return this._initialMappingName;
  }

  set initialMappingName(value: string) {
    this._initialMappingName = value;
  }

  get initialData(): any {
    return this._initialData;
  }

  set initialData(value: any) {
    this._initialData = value;
  }

  get data(): any {
    return this._data;
  }

  set data(value: any) {
    this._data = value;
  }

  get operations(): Action {
    return this._operations;
  }

  set operations(value: Action) {
    this._operations = value;
  }

  get uploadGroupId(): string {
    return this._uploadGroupId;
  }

  set uploadGroupId(value: string) {
    this._uploadGroupId = value;
  }

  get afterInitialRequest(): Operation[] {
    return this._afterInitialRequest;
  }

  set afterInitialRequest(value: Operation[]) {
    this._afterInitialRequest = value;
  }

  get afterInitialRequestHandleErrorStatus(): Operation[] {
    return this._afterInitialRequestHandleErrorStatus;
  }

  set afterInitialRequestHandleErrorStatus(value: Operation[]) {
    this._afterInitialRequestHandleErrorStatus = value;
  }

  get afterPingRequestHandleErrorStatus(): Operation[] {
    return this._afterPingRequestHandleErrorStatus;
  }

  set afterPingRequestHandleErrorStatus(value: Operation[]) {
    this._afterPingRequestHandleErrorStatus = value;
  }

  get progressOperations(): Operation[] {
    return this._progressOperations;
  }

  set progressOperations(value: Operation[]) {
    this._progressOperations = value;
  }

  get handleFileRejection(): Operation[] {
    return this._handleFileRejection;
  }

  set handleFileRejection(value: Operation[]) {
    this._handleFileRejection = value;
  }
}

export default Upload;
